import React, { useState, useEffect, useRef } from 'react';
import selectionStorage from '../utils/selectionStorage';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import accountService from '../services/accountService';
import permissionService from '../services/permissionService';
import partnerService from '../services/partnerService';
import HubLogoMark from './shared/HubLogoMark';
import useDropdownNav from './shared/useDropdownNav';
import useAccountsApprover from '../hooks/useAccountsApprover';
import './Navbar.css';

const Navbar = () => {
  const { user, logout, isAuthenticated, isSuperAdmin, isPartner, solePartnerId, vendorOf, partners: memberPartners } = useAuth();
  // A PERSON AT A VENDOR picks the partner they are a vendor for in the partner switcher, but is not a member
  // of it: they see only the chains and hotels the vendor was granted, Vendor Settings instead of Partner
  // Settings, and no Marketing Pack. `memberPartners` are the partners with a partner-level permission row.
  const isVendorPerson = !!vendorOf;
  const usesPartnerSwitcher = isPartner || isVendorPerson;
  const isMemberOf = (pid) => !!pid && (memberPartners || []).some(p => Number(p.partnerId) === Number(pid));
  // Admin > Billing > Accounts Agent and QuickBooks Customers are for the accounts agent's approvers only (Jamie, David).
  const { isApprover: isAccountsApprover } = useAccountsApprover();
  const { selectedAccount, selectAccount, accounts, setAccounts, isChainAdmin, isAnyAdmin, accountsRefreshKey, selectedPartner, selectPartner, selectedHotel, selectHotel, hotels } = useAccount();
  const navigate = useNavigate();
  const location = useLocation();
  // A pipeline template opened from Pipelines (?from=pipelines | implementation) belongs to Pipelines in the sidebar.
  const templateInPipelines = location.pathname.startsWith('/pipeline-templates')
    && ['pipelines', 'implementation'].includes(new URLSearchParams(location.search).get('from'));
  const [isAdminExpanded, setIsAdminExpanded] = useState(false);
  const [isProfilesExpanded, setIsProfilesExpanded] = useState(false);
  const [isPlatformExpanded, setIsPlatformExpanded] = useState(false);
  const [isBillingExpanded, setIsBillingExpanded] = useState(false);
  const [isInfraExpanded, setIsInfraExpanded] = useState(false);
  const [isOpenSearchExpanded, setIsOpenSearchExpanded] = useState(false);
  const [isQueuesExpanded, setIsQueuesExpanded] = useState(false);
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isPartnerDropdownOpen, setIsPartnerDropdownOpen] = useState(false);
  const [isHotelDropdownOpen, setIsHotelDropdownOpen] = useState(false);
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');
  const [partnerSearchQuery, setPartnerSearchQuery] = useState('');
  const [hotelSearchQuery, setHotelSearchQuery] = useState('');
  const [partners, setPartners] = useState([]);
  const tenantDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const partnerDropdownRef = useRef(null);
  const hotelDropdownRef = useRef(null);

  // Auto-expand Admin section if on admin, partners, users, or settings page
  useEffect(() => {
    if (location.pathname.startsWith('/admin') ||
        location.pathname.startsWith('/partners') ||
        location.pathname.startsWith('/users') ||
        location.pathname.startsWith('/admin/prospects')) {
      setIsAdminExpanded(true);
    }
    if (location.pathname.startsWith('/users') ||
        location.pathname.startsWith('/partners') ||
        location.pathname.startsWith('/admin/prospects')) {
      setIsProfilesExpanded(true);
    }
    if (location.pathname.startsWith('/admin/vaults') ||
        location.pathname.startsWith('/admin/canonical') ||
        location.pathname.startsWith('/admin/knowledge-base') ||
        location.pathname.startsWith('/admin/marketplace') ||
        location.pathname.startsWith('/admin/products') ||
        location.pathname.startsWith('/admin/sync-templates') ||
        location.pathname.startsWith('/admin/sync-scopes') ||
        location.pathname.startsWith('/admin/sync-processing-steps') ||
        location.pathname.startsWith('/admin/default-rev-share') ||
        location.pathname.startsWith('/admin/contracts') ||
        location.pathname.startsWith('/admin/syncconnect') ||
        location.pathname.startsWith('/admin/currencies') ||
        location.pathname.startsWith('/admin/mcp-service') ||
        location.pathname.startsWith('/admin/audit-trail')) {
      setIsPlatformExpanded(true);
    }
    // Billing. Its own group rather than a Platform entry: these are commercial screens (what the
    // customer is charged for), not configuration, and they will gain siblings — invoices, rev share —
    // that would read oddly filed under Platform.
    if (location.pathname.startsWith('/admin/billing')) {
      setIsBillingExpanded(true);
    }
    if (location.pathname.startsWith('/admin/opensearch') ||
        location.pathname.startsWith('/admin/sqs') ||
        location.pathname.startsWith('/admin/oxi-dequeue-queue') ||
        location.pathname.startsWith('/admin/payment-token-swap') ||
        location.pathname.startsWith('/admin/dns-and-ip') ||
        location.pathname.startsWith('/admin/s3-viewer') ||
        location.pathname.startsWith('/admin/athena') ||
        (location.pathname.startsWith('/pipeline-templates') && !templateInPipelines) ||
        location.pathname.startsWith('/admin/receivers-pollers') ||
        location.pathname.startsWith('/admin/code') ||
        location.pathname.startsWith('/admin/v2-connectors') ||
        location.pathname.startsWith('/admin/legacy-ingress') ||
        location.pathname.startsWith('/admin/sync-diverter') ||
        location.pathname.startsWith('/admin/streaming-clients')) {
      setIsInfraExpanded(true);
    }
    if (location.pathname.startsWith('/admin/opensearch/cwlogs') ||
        location.pathname.startsWith('/admin/opensearch/spr-logs') ||
        location.pathname.startsWith('/admin/payment-token-swap')) {
      setIsOpenSearchExpanded(true);
    }
    if (location.pathname.startsWith('/admin/sqs') ||
        location.pathname.startsWith('/admin/oxi-dequeue-queue')) {
      setIsQueuesExpanded(true);
    }
  }, [location.pathname]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tenantDropdownRef.current && !tenantDropdownRef.current.contains(event.target)) {
        setIsTenantDropdownOpen(false);
        setTenantSearchQuery('');
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(event.target)) {
        setIsPartnerDropdownOpen(false);
        setPartnerSearchQuery('');
      }
      if (hotelDropdownRef.current && !hotelDropdownRef.current.contains(event.target)) {
        setIsHotelDropdownOpen(false);
        setHotelSearchQuery('');
      }
    };

    // Esc closes whichever is open, as a list should.
    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      setIsTenantDropdownOpen(false); setTenantSearchQuery('');
      setIsUserDropdownOpen(false);
      setIsPartnerDropdownOpen(false); setPartnerSearchQuery('');
      setIsHotelDropdownOpen(false); setHotelSearchQuery('');
    };

    if (isTenantDropdownOpen || isUserDropdownOpen || isPartnerDropdownOpen || isHotelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
    // isHotelDropdownOpen belongs here with the others: without it the listener was never attached while only the
    // hotel picker was open, so it ignored outside clicks and stayed open under the chain picker (HIL-129).
  }, [isTenantDropdownOpen, isUserDropdownOpen, isPartnerDropdownOpen, isHotelDropdownOpen]);

  // Fetch partners for superadmins (full list) and partner users (all their permitted partners)
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isSuperAdmin) {
      partnerService.getAllPartners()
        .then(res => {
          const list = res.data?.partners || res.partners || res.data || [];
          setPartners(list);
        })
        .catch(err => console.error('Error fetching partners:', err));
    } else if (usesPartnerSwitcher) {
      partnerService.getMyPartners()
        .then(res => {
          const list = res.data?.partners || [];
          setPartners(list);
          // Auto-select: use stored selectedPartner if valid, else first partner
          const stored = parseInt(selectionStorage.getPartnerId());
          const valid = stored && list.find(p => p.partnerid === stored);
          if (!valid && list.length > 0) {
            selectPartner(list[0].partnerid);
          }
        })
        .catch(err => console.error('Error fetching partners:', err));
    }
  }, [isAuthenticated, isSuperAdmin, isPartner, isVendorPerson]);

  // Fetch hotel chains (accounts) on mount, and when partner filter changes
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        // Always an array: a partner user with several partners and none chosen yet has no pid, and has no chains
        // to show until they pick one.
        let accountList = [];
        let awaitingPartner = false;
        if (isSuperAdmin) {
          const filters = { activeOnly: true };
          if (selectedPartner) filters.partnerid = selectedPartner;
          const response = await accountService.getAllAccounts(filters);
          accountList = response.accounts || [];
        } else if (usesPartnerSwitcher) {
          const pid = selectedPartner || solePartnerId;
          if (!pid) {
            awaitingPartner = true;
          } else if (isMemberOf(pid)) {
            // A member of the selected partner: every chain under it.
            const response = await accountService.getAllAccounts({ activeOnly: true, partnerid: pid });
            accountList = response.accounts || [];
          } else {
            // Not a member (a person at its vendor, or somebody granted a chain or hotel under it): only the
            // chains their own grants reach, under the partner they have chosen — never the partner's estate.
            const response = await permissionService.getMyChains();
            accountList = (response.chains || []).filter(c => Number(c.partnerid) === Number(pid));
          }
        } else {
          const response = await permissionService.getMyChains();
          accountList = response.chains || [];
        }
        setAccounts(accountList);
        if (!isSuperAdmin && !usesPartnerSwitcher && accountList.length === 0) {
          navigate('/no-permissions', { replace: true });
        }
        if (awaitingPartner) return;
        // If the stored selected account is not in the new list, clear it
        const currentAccountId = parseInt(selectionStorage.getAccountId());
        if (currentAccountId && !accountList.find(a => a.id === currentAccountId)) {
          selectAccount(null);
        }
        // Auto-select if there is exactly one account
        if (accountList.length === 1) {
          selectAccount(accountList[0].id);
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };

    if (isAuthenticated && user) {
      fetchAccounts();
    }
  }, [isAuthenticated, user, isSuperAdmin, isPartner, isVendorPerson, solePartnerId, setAccounts, accountsRefreshKey, selectedPartner]);

  const handleAccountChange = (accountId) => {
    selectAccount(accountId);
    setIsTenantDropdownOpen(false);
    setTenantSearchQuery('');
    navigate('/dashboard');
  };

  const handleHotelChange = (hotelId) => {
    selectHotel(hotelId);
    setIsHotelDropdownOpen(false);
    setHotelSearchQuery('');
    navigate('/dashboard');
  };

  const getSelectedHotelName = () => {
    if (!selectedHotel) return 'All Hotels';
    const h = hotels.find(h => h.hotelid === selectedHotel);
    return h ? (h.hotelname || 'Unknown') : 'All Hotels';
  };

  // Fuzzy search function - checks if search characters appear in order in the target string
  const fuzzyMatch = (str, query) => {
    if (!query) return true;
    const strLower = str.toLowerCase();
    const queryLower = query.toLowerCase();
    if (strLower.includes(queryLower)) return true;
    let queryIndex = 0;
    for (let i = 0; i < strLower.length && queryIndex < queryLower.length; i++) {
      if (strLower[i] === queryLower[queryIndex]) queryIndex++;
    }
    return queryIndex === queryLower.length;
  };

  const filteredHotels = hotels.filter(h => {
    if (!hotelSearchQuery) return true;
    return fuzzyMatch(h.hotelname || '', hotelSearchQuery) || fuzzyMatch(h.hotelcode || '', hotelSearchQuery);
  });

  const handlePartnerChange = (partnerId) => {
    selectPartner(partnerId);
    setIsPartnerDropdownOpen(false);
    setPartnerSearchQuery('');
    navigate('/dashboard');
  };

  const togglePartnerDropdown = () => {
    setIsPartnerDropdownOpen(open => !open);
    if (isPartnerDropdownOpen) setPartnerSearchQuery('');
  };

  const getSelectedPartnerName = () => {
    if (!selectedPartner) return 'All Partners';
    const p = partners.find(p => p.partnerid === selectedPartner);
    return p ? (p.companyname || p.name || 'Unknown') : 'All Partners';
  };

  const toggleTenantDropdown = () => {
    setIsTenantDropdownOpen(!isTenantDropdownOpen);
    if (!isTenantDropdownOpen) {
      setTenantSearchQuery('');
    }
  };

  const getSelectedAccountName = () => {
    if (!selectedAccount) return 'Select Hotel Chain';
    const account = accounts.find(acc => acc.id === selectedAccount);
    return account ? (account.name || 'Unknown') : 'Select Hotel Chain';
  };

  // Filter accounts based on search query
  const filteredAccounts = accounts.filter((account) => {
    if (!tenantSearchQuery) return true;
    const name = account.name || '';
    const username = account.username || '';
    return fuzzyMatch(name, tenantSearchQuery) || (username && fuzzyMatch(username, tenantSearchQuery));
  });

  // Filter partners based on partner search query
  const filteredPartners = partners.filter((p) => {
    if (!partnerSearchQuery) return true;
    return fuzzyMatch(p.companyname || p.name || '', partnerSearchQuery);
  });

  // ── Keyboard navigation for the three pickers ─────────────────────────────
  //
  // Each list is described here in EXACTLY the order it renders, including the "All …" pseudo-option
  // (which only appears when the search box is empty). useDropdownNav owns the highlight; the JSX below
  // reads isActive(i) and spreads navProps(i). The index offset in the JSX is `hasAllRow ? 1 : 0`,
  // which is why the pseudo-option has to be part of this array rather than special-cased in the hook.
  // There are TWO partner dropdowns sharing this state — the superadmin one (which offers "All
  // Partners") and the multi-partner user one (which does not, because a partner user has no
  // all-partners view). Only one is ever rendered, so the "All" row is included here on exactly the
  // same condition the JSX uses to render it. Get this wrong and every index is off by one: arrowing to
  // the first visible partner would fire handlePartnerChange(null).
  const partnerHasAllRow = isSuperAdmin && !partnerSearchQuery;
  const partnerNavItems = React.useMemo(() => ([
    ...(partnerHasAllRow ? [{ key: 'all', select: () => handlePartnerChange(null) }] : []),
    ...filteredPartners.map(p => ({ key: p.partnerid, select: () => handlePartnerChange(p.partnerid) })),
  ]), [partnerHasAllRow, filteredPartners]);

  const accountNavItems = React.useMemo(() => ([
    ...(tenantSearchQuery ? [] : [{ key: 'all', select: () => handleAccountChange(null) }]),
    ...filteredAccounts.map(a => ({ key: a.id, select: () => handleAccountChange(a.id) })),
  ]), [tenantSearchQuery, filteredAccounts]);

  const hotelNavItems = React.useMemo(() => ([
    ...(hotelSearchQuery ? [] : [{ key: 'all', select: () => handleHotelChange(null) }]),
    ...filteredHotels.map(h => ({ key: h.hotelid, select: () => handleHotelChange(h.hotelid) })),
  ]), [hotelSearchQuery, filteredHotels]);

  const partnerNav = useDropdownNav({
    isOpen: isPartnerDropdownOpen, items: partnerNavItems, query: partnerSearchQuery,
    onClose: () => { setIsPartnerDropdownOpen(false); setPartnerSearchQuery(''); },
  });
  const accountNav = useDropdownNav({
    isOpen: isTenantDropdownOpen, items: accountNavItems, query: tenantSearchQuery,
    onClose: () => { setIsTenantDropdownOpen(false); setTenantSearchQuery(''); },
  });
  const hotelNav = useDropdownNav({
    isOpen: isHotelDropdownOpen, items: hotelNavItems, query: hotelSearchQuery,
    onClose: () => { setIsHotelDropdownOpen(false); setHotelSearchQuery(''); },
  });

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  const handleLogout = () => {
    setIsUserDropdownOpen(false);
    logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    setIsUserDropdownOpen(false);
    navigate('/profile');
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  // Don't render navbar on landing page, login page, or when not authenticated
  if (!isAuthenticated || location.pathname === '/login' || location.pathname === '/') {
    return null;
  }

  // A PIPELINE TEMPLATE OPENED FROM PIPELINES (?from=pipelines | implementation) is part of Pipelines, not of the
  // Admin › Infrastructure section that holds the template list — the sidebar stays where the reader was.
  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Left Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-logo">
            <HubLogoMark size={32} />
            <span className="sidebar-brand"><span style={{color:'#3b6ef5'}}>Hub</span> by HConnect</span>
          </Link>
        </div>

        <nav className="sidebar-nav">
          <Link to="/dashboard" className={`sidebar-link ${isActive('/dashboard') ? 'active' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 9l7-7 7 7v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Home</span>
          </Link>

          <Link to="/hotels" className={`sidebar-link ${isActive('/hotels') ? 'active' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M2 18V7l8-5 8 5v11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 18v-5h4v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 10h2M12 10h2M6 13h2M12 13h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span>Hotels</span>
          </Link>

          <div className="sidebar-section-header">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3 7l7-4 7 4-7 4-7-4z" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round"/>
              <path d="M3 13l7 4 7-4M3 10l7 4 7-4" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round"/>
            </svg>
            <span>Products</span>
          </div>

          <Link to="/vaults" className={`sidebar-link sidebar-sublink ${isActive('/vaults') ? 'active' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="2" y="2" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
              <rect x="6" y="6" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span className="sidebar-link-text">
              <span className="sidebar-link-title">Vaults</span>
              <span className="sidebar-link-subtitle">Archive old PMS systems</span>
            </span>
          </Link>

          <Link to="/apps" className={`sidebar-link sidebar-sublink ${isActive('/apps') ? 'active' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="2" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75"/>
              <rect x="11" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75"/>
              <rect x="2" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75"/>
              <rect x="11" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.75"/>
            </svg>
            <span className="sidebar-link-text">
              <span className="sidebar-link-title">Apps</span>
              <span className="sidebar-link-subtitle">Automate hotel operations</span>
            </span>
          </Link>

          <Link to="/sync" className={`sidebar-link sidebar-sublink ${isActive('/sync') ? 'active' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10a6 6 0 0 1 6-6 6 6 0 0 1 4.24 1.76L16 4v4h-4l1.42-1.42A4 4 0 1 0 14 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 10a6 6 0 0 1-6 6 6 6 0 0 1-4.24-1.76L4 16v-4h4L6.58 13.42A4 4 0 1 0 6 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="sidebar-link-text">
              <span className="sidebar-link-title">Sync</span>
              <span className="sidebar-link-subtitle">Migrate to new PMS system</span>
            </span>
          </Link>

          {/* Pipelines and Universal API are superadmin-only for
              now — can be opened up later. */}
          {isSuperAdmin && (
            <Link to="/pipelines" className={`sidebar-link sidebar-sublink ${isActive('/pipelines') || templateInPipelines ? 'active' : ''}`}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="4" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.75"/>
                <circle cx="16" cy="4" r="2.5" stroke="currentColor" strokeWidth="1.75"/>
                <circle cx="16" cy="16" r="2.5" stroke="currentColor" strokeWidth="1.75"/>
                <path d="M6.2 8.8 13.8 5.2M6.2 11.2 13.8 14.8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
              </svg>
              <span className="sidebar-link-text">
                <span className="sidebar-link-title">Pipelines</span>
                <span className="sidebar-link-subtitle">Move messages between systems</span>
              </span>
            </Link>
          )}

          {/* MCP as a Service — a customer product, superadmin only for now (HIL-140); once open, every user with a
              chain, and the API decides who may change what. */}
          {isSuperAdmin && (
          <Link to="/mcp-service" className={`sidebar-link sidebar-sublink ${isActive('/mcp-service') ? 'active' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="3" y="4" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.75"/>
              <path d="M7 17h6M10 14v3M7 9h.01M10 9h.01M13 9h.01" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
            <span className="sidebar-link-text">
              <span className="sidebar-link-title">MCP as a Service</span>
              <span className="sidebar-link-subtitle">AI assistants book your hotels</span>
            </span>
          </Link>
          )}

          {/* Universal API. The screens moved here out of Legacy (2026-09-07) — this is the offering,
              not a pre-hub implementation being kept alive, so it sits with the other products and the
              Legacy section is connectors only.

              ONE ENTRY, not one per screen. Authentication and Access and Logs are TABS on this page,
              and a sidebar entry per tab says the same thing twice — the tab strip is already the
              navigation between them, and the second entry only made the sidebar longer while leaving
              the third tab unrepresented, which reads as a screen that is missing. */}
          {isSuperAdmin && (
            <Link to="/universal-api" className={`sidebar-link sidebar-sublink ${isActive('/universal-api') ? 'active' : ''}`}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.75"/>
                <path d="M2 10h16M10 2c2.2 2.2 3.3 5 3.3 8s-1.1 5.8-3.3 8c-2.2-2.2-3.3-5-3.3-8S7.8 4.2 10 2z" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="sidebar-link-text">
                <span className="sidebar-link-title">Universal API</span>
                <span className="sidebar-link-subtitle">One interface to all PMS systems</span>
              </span>
            </Link>
          )}

          {/* LEGACY IS NO LONGER IN THE SIDEBAR. It held the Universal API until 2026-09-07 and then
              only Connectors, and connectors are maintained from the screens that own them rather than
              browsed from a standing menu of things we are trying to retire. /legacy/connectors is still
              routed, so a link or a bookmark to it works. */}

          {(isSuperAdmin || isPartner || isChainAdmin) && selectedAccount && (
            <>
              <div className="sidebar-separator" />
              <Link to="/chain-settings" className={`sidebar-link ${isActive('/chain-settings') ? 'active' : ''}`}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M17 10a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>Hotel Chain Settings</span>
              </Link>
            </>
          )}

          {(isPartner || isSuperAdmin || isVendorPerson) && (
            <>
              <div className="sidebar-separator" />
              {/* Partner Settings is for the partner's own people. A person at one of its vendors sees Vendor
                  Settings instead — and only while the partner they are a vendor for is the one chosen. */}
              {selectedPartner && isVendorPerson && Number(selectedPartner) === Number(vendorOf.partnerId) && (
                <Link to="/vendor-settings" className={`sidebar-link ${isActive('/vendor-settings') ? 'active' : ''}`}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M17 10a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <span>Vendor Settings</span>
                </Link>
              )}
              {selectedPartner && (isSuperAdmin || isMemberOf(selectedPartner)) && (
                <Link to="/partner-settings" className={`sidebar-link ${isActive('/partner-settings') ? 'active' : ''}`}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M17 10a7 7 0 11-14 0 7 7 0 0114 0z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <span>Partner Settings</span>
                </Link>
              )}
              {(isSuperAdmin || isPartner) && (
                <Link to="/marketing-pack" className={`sidebar-link ${isActive('/marketing-pack') ? 'active' : ''}`}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M4 4h12v2H4zM4 8h8v2H4zM4 12h10v2H4zM4 16h6v2H4z" fill="currentColor"/>
                    <path d="M15 11l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Marketing Pack</span>
                </Link>
              )}

              {/* The Migration Toolkit is no longer a menu entry: it lives inside each sync project whose template uses
                  it (Sync › project › Toolkit), working through that project's own OPERA Cloud connection. */}
              <div className="sidebar-separator" />
            </>
          )}

          {/* Rotana — visible only when Rotana chain (account 10) is selected */}
          {selectedAccount === 10 && (
            <>
              <Link to="/rotana" className={`sidebar-link ${isActive('/rotana') ? 'active' : ''}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/>
                  <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Rotana</span>
              </Link>
              <div className="sidebar-separator" />
            </>
          )}

          {/* Admin Section - Only visible to Super Admins */}
          {isSuperAdmin && (
            <div className="sidebar-admin-section">
              <button
                className="sidebar-admin-header"
                onClick={() => setIsAdminExpanded(!isAdminExpanded)}
              >
                <div className="sidebar-admin-header-content">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2a2 2 0 012 2v1a2 2 0 01-2 2 2 2 0 01-2-2V4a2 2 0 012-2zm0 10a2 2 0 012 2v1a2 2 0 01-2 2 2 2 0 01-2-2v-1a2 2 0 012-2z" fill="currentColor"/>
                    <circle cx="4" cy="10" r="2" fill="currentColor"/>
                    <circle cx="16" cy="10" r="2" fill="currentColor"/>
                  </svg>
                  <span>Admin</span>
                </div>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className={`sidebar-admin-chevron ${isAdminExpanded ? 'expanded' : ''}`}
                >
                  <path d="M4 5l2 2 2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>

              {isAdminExpanded && (
                <div className="sidebar-admin-submenu">
                  <button
                    className="sidebar-link sidebar-sublink sidebar-admin-group-toggle"
                    style={{ justifyContent: 'flex-start' }}
                    onClick={() => setIsProfilesExpanded(e => !e)}
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V18h14v-1.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                    </svg>
                    <span>Profiles</span>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`sidebar-admin-chevron${isProfilesExpanded ? ' expanded' : ''}`} style={{ marginLeft: 'auto' }}>
                      <path d="M4 5l2 2 2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>

                  {isProfilesExpanded && (
                    <>
                      <Link to="/users" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/users') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                        </svg>
                        <span>Users</span>
                      </Link>

                      <Link to="/partners" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/partners') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V18h14v-1.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                        </svg>
                        <span>Partners</span>
                      </Link>

                      <Link to="/admin/prospects" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/prospects') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M12 12c2.5 0 4.5 1 4.5 2.5V16H3.5v-1.5C3.5 13 5.5 12 8 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M15 5c1.66 0 3 1.34 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M5 5C3.34 5 2 6.34 2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Prospects</span>
                      </Link>
                    </>
                  )}

                  <button
                    className="sidebar-link sidebar-sublink sidebar-admin-group-toggle"
                    style={{ justifyContent: 'flex-start' }}
                    onClick={() => setIsPlatformExpanded(e => !e)}
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                      <path d="M3 5h14M3 10h10M3 15h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="15" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                    <span>Platform</span>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`sidebar-admin-chevron${isPlatformExpanded ? ' expanded' : ''}`} style={{ marginLeft: 'auto' }}>
                      <path d="M4 5l2 2 2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>

                  {isPlatformExpanded && (
                    <>
                      <Link to="/admin/vaults" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/vaults') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z" fill="currentColor"/>
                        </svg>
                        <span>Vault System</span>
                      </Link>

                      <Link to="/admin/canonical" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/canonical') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <rect x="3" y="4" width="14" height="2" rx="1" fill="currentColor"/>
                          <rect x="3" y="9" width="14" height="2" rx="1" fill="currentColor"/>
                          <rect x="3" y="14" width="10" height="2" rx="1" fill="currentColor"/>
                        </svg>
                        <span>Canonical</span>
                      </Link>

                      <Link to="/admin/knowledge-base" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/knowledge-base') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M4 4h9a2 2 0 0 1 2 2v10H6a2 2 0 0 0-2 2V4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                          <circle cx="15.5" cy="14.5" r="3" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M15.5 13v1.5l1 .8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                        <span>Knowledge Base</span>
                      </Link>

                      {/* The Universal API's DEFINITION — the routes and operations that make up the API
                          surface. Deliberately separate from Legacy > Universal API, which provisions a
                          hotel's authentication against it: the surface is one platform-wide artefact,
                          the credentials are per property, and editing them on one screen made a
                          chain-wide change look like a property setting. */}
                      <Link to="/admin/universal-api" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/universal-api') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M7 5 3 10l4 5M13 5l4 5-4 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M11.5 4 8.5 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Universal API</span>
                      </Link>

                      {/* The payloads a module carries, as their own screen rather than a tab inside the
                          route tree. Indented under Universal API because a payload belongs to one of its
                          modules — it is not a separate artefact, it is what those routes carry. */}
                      <Link to="/admin/universal-api/payloads" className={`sidebar-link sidebar-sublink sidebar-sublink-deep ${isActive('/admin/universal-api/payloads') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M5 4.5A1.5 1.5 0 0 1 6.5 3H12l3.5 3.5V15A1.5 1.5 0 0 1 14 16.5H6.5A1.5 1.5 0 0 1 5 15V4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                          <path d="M12 3v3.5h3.5M7.5 10.5h5M7.5 13h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Routes and Payloads</span>
                      </Link>

                      {/* The code behind the API, beside the payloads it carries. Indented under
                          Universal API for the same reason Routes and Payloads is: an integration is not a
                          separate artefact, it is how one of these systems is reached. */}
                      <Link to="/admin/universal-api/integrations" className={`sidebar-link sidebar-sublink sidebar-sublink-deep ${isActive('/admin/universal-api/integrations') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M8 4 4.5 10 8 16M12 4l3.5 6-3.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="10" cy="10" r="1.4" fill="currentColor"/>
                        </svg>
                        <span>UAPI Integrations</span>
                      </Link>

                      {/* hconnect's side of MCP as a Service — the default agreement and hotels' proposals. The
                          chains' and hotels' own settings are on the MCP as a Service product screen. */}
                      <Link to="/admin/mcp-service" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/mcp-service') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <rect x="3" y="4" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M7 17h6M10 14v3M7.5 9h.01M12.5 9h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>MCP as a Service</span>
                      </Link>

                      <Link to="/admin/marketplace" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/marketplace') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <circle cx="16" cy="5" r="2" fill="currentColor"/>
                          <circle cx="16" cy="15" r="2" fill="currentColor"/>
                        </svg>
                        <span>Marketplace</span>
                      </Link>

                      <Link to="/admin/marketplace-action-types" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/marketplace-action-types') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M4 6h8M4 10h8M4 14h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <circle cx="15.5" cy="6" r="1.5" fill="currentColor"/>
                          <circle cx="15.5" cy="10" r="1.5" fill="currentColor"/>
                        </svg>
                        <span>Marketplace Action Types</span>
                      </Link>

                      <Link to="/admin/currencies" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/currencies') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M12.5 7.5c-.4-.9-1.4-1.5-2.5-1.5-1.4 0-2.5.8-2.5 2s1.1 1.6 2.5 2 2.5.8 2.5 2-1.1 2-2.5 2c-1.1 0-2.1-.6-2.5-1.5M10 4.5v11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Currencies</span>
                      </Link>

                      <Link to="/admin/audit-trail" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/audit-trail') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M5 3h7l3 3v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                          <path d="M7 9h6M7 12h6M7 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Audit Trail</span>
                      </Link>

                      <Link to="/admin/products" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/products') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <rect x="3" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                          <rect x="11" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                          <rect x="3" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                          <rect x="11" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                        <span>Products</span>
                      </Link>

                      <Link to="/admin/sync-templates" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/sync-templates') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M4 6h12M4 10h8M4 14h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Sync</span>
                      </Link>

                      <Link to="/admin/sync-scopes" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/sync-scopes') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                          <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M10 1.5v3M10 15.5v3M1.5 10h3M15.5 10h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Sync Scopes</span>
                      </Link>

                      <Link to="/admin/default-rev-share" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/default-rev-share') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M3 10h14M10 3l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Default Rev Share</span>
                      </Link>

                      <Link to="/admin/contracts" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/contracts') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M5 3h10a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M7 7h6M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Contracts</span>
                      </Link>

                      {/* Ad-hoc read-only SQL run by a project's SyncConnect against the property's own database. */}
                      <Link to="/admin/syncconnect" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/syncconnect') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <ellipse cx="10" cy="5" rx="6" ry="2.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M4 5v10c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5V5M4 10c0 1.38 2.69 2.5 6 2.5s6-1.12 6-2.5" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                        <span>SyncConnect</span>
                      </Link>
                    </>
                  )}

                  <button
                    className="sidebar-link sidebar-sublink sidebar-admin-group-toggle"
                    style={{ justifyContent: 'flex-start' }}
                    onClick={() => setIsBillingExpanded(e => !e)}
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                      <rect x="3" y="4" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M3 8h14" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M6 12h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>Billing</span>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`sidebar-admin-chevron${isBillingExpanded ? ' expanded' : ''}`} style={{ marginLeft: 'auto' }}>
                      <path d="M4 5l2 2 2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>

                  {isBillingExpanded && (
                    <>
                      <Link to="/admin/billing/ohip-transactions" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/billing/ohip-transactions') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M3 15l4-5 3 3 4-6 3 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M3 17h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>OHIP Transactions</span>
                      </Link>

                      {isAccountsApprover && (
                      <Link to="/admin/billing/accounts-agent" title="Visible to accounts approvers only" className={`sidebar-link sidebar-sublink sidebar-sublink-nested approver-only ${isActive('/admin/billing/accounts-agent') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <rect x="4" y="3" width="12" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M7 7h6M7 10h6M7 13h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Accounts Agent</span>
                      </Link>
                      )}

                      {isAccountsApprover && (
                      <Link to="/admin/billing/quickbooks-customers" title="Visible to accounts approvers only" className={`sidebar-link sidebar-sublink sidebar-sublink-nested approver-only ${isActive('/admin/billing/quickbooks-customers') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <circle cx="7.5" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M3 16c0-2.5 2-4.5 4.5-4.5S12 13.5 12 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M13 6.5h4M13 9.5h4M14.5 12.5h2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>QuickBooks Customers</span>
                      </Link>
                      )}

                      <Link to="/admin/billing/hotel-groups" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/billing/hotel-groups') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <rect x="3" y="7" width="5" height="9" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                          <rect x="12" y="4" width="5" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M3 17h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>OHIP Environments</span>
                      </Link>

                      <Link to="/admin/billing/extraction-runs" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/billing/extraction-runs') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M10 3a7 7 0 1 0 7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M10 6v4l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14.5 3.5 17 6l-3 .5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Extraction Runs</span>
                      </Link>

                      <Link to="/admin/billing/invoices" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/billing/invoices') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M5 2.5h7l3 3V17a.5.5 0 0 1-.5.5h-9A.5.5 0 0 1 5 17V3a.5.5 0 0 1 .5-.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                          <path d="M12 2.5V6h3" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                          <path d="M7.5 10h5M7.5 13h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Invoices</span>
                      </Link>
                    </>
                  )}


                  <button
                    className="sidebar-link sidebar-sublink sidebar-admin-group-toggle"
                    style={{ justifyContent: 'flex-start' }}
                    onClick={() => setIsInfraExpanded(e => !e)}
                  >
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                      <rect x="2" y="3" width="16" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                      <rect x="2" y="12" width="16" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                      <circle cx="15" cy="5.5" r="1" fill="currentColor"/>
                      <circle cx="15" cy="14.5" r="1" fill="currentColor"/>
                    </svg>
                    <span>Infra</span>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`sidebar-admin-chevron${isInfraExpanded ? ' expanded' : ''}`} style={{ marginLeft: 'auto' }}>
                      <path d="M4 5l2 2 2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>

                  {isInfraExpanded && (
                    <>
                      <button
                        className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-admin-group-toggle`}
                        style={{ justifyContent: 'flex-start' }}
                        onClick={() => setIsOpenSearchExpanded(e => !e)}
                      >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <circle cx="9" cy="9" r="5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M7 9h4M9 7v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>OpenSearch</span>
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`sidebar-admin-chevron${isOpenSearchExpanded ? ' expanded' : ''}`} style={{ marginLeft: 'auto' }}>
                          <path d="M4 5l2 2 2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>

                      {isOpenSearchExpanded && (
                        <>
                          <Link to="/admin/opensearch/cwlogs" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/opensearch/cwlogs') ? 'active' : ''}`}>
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                              <path d="M3 5h14M3 9h14M3 13h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <span>Cloudwatch Logs</span>
                          </Link>
                          <Link to="/admin/opensearch/spr-logs" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/opensearch/spr-logs') ? 'active' : ''}`}>
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                              <path d="M3 5h14M3 9h14M3 13h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                            <span>SPR Logs</span>
                          </Link>
                          <Link to="/admin/payment-token-swap" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/payment-token-swap') ? 'active' : ''}`}>
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                              <path d="M4 7h9l-2-2M16 13H7l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>Payment Token Swap</span>
                          </Link>
                        </>
                      )}

                      <button
                        className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-admin-group-toggle`}
                        style={{ justifyContent: 'flex-start' }}
                        onClick={() => setIsQueuesExpanded(e => !e)}
                      >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <rect x="2" y="5" width="16" height="3" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                          <rect x="2" y="10" width="16" height="3" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                          <rect x="2" y="15" width="10" height="3" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                        <span>Queues</span>
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`sidebar-admin-chevron${isQueuesExpanded ? ' expanded' : ''}`} style={{ marginLeft: 'auto' }}>
                          <path d="M4 5l2 2 2-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>

                      {isQueuesExpanded && (
                        <>
                          <Link to="/admin/sqs" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/sqs') ? 'active' : ''}`}>
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                              <rect x="2" y="5" width="16" height="3" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                              <rect x="2" y="10" width="16" height="3" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                              <rect x="2" y="15" width="10" height="3" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                            <span>SQS Queues</span>
                          </Link>
                          <Link to="/admin/oxi-dequeue-queue" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/oxi-dequeue-queue') ? 'active' : ''}`}>
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                              <path d="M3 6h14M3 10h14M3 14h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              <circle cx="16" cy="14" r="3" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M15 14h2M16 13v2" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/>
                            </svg>
                            <span>Dequeue Queue</span>
                          </Link>
                        </>
                      )}
                      <Link to="/admin/dns-and-ip" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/dns-and-ip') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M10 6v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M4.5 4.5l11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0"/>
                          <path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>DNS and IP</span>
                      </Link>
                      <Link to="/admin/s3-viewer" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/s3-viewer') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <ellipse cx="10" cy="5" rx="6.5" ry="2.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M3.5 5v10c0 1.4 2.9 2.5 6.5 2.5s6.5-1.1 6.5-2.5V5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M3.5 10c0 1.4 2.9 2.5 6.5 2.5s6.5-1.1 6.5-2.5" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                        <span>S3 Viewer</span>
                      </Link>
                      <Link to="/admin/athena" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/athena') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <rect x="3" y="3.5" width="14" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M3 7.5h14M8 7.5v9" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                        <span>Athena</span>
                      </Link>
                      <Link to="/admin/code" className={`sidebar-link sidebar-sublink sidebar-sublink-nested ${isActive('/admin/code') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M7 6l-4 4 4 4M13 6l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Code</span>
                      </Link>
                      <Link to="/admin/receivers-pollers" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/receivers-pollers') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M10 13v4M6 17h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M4 10a6 6 0 0 1 6-6M4 10a6 6 0 0 0 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
                          <path d="M13 4a8.5 8.5 0 0 1 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Receivers/Pollers</span>
                      </Link>
                      <Link to="/admin/sync-processing-steps" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/sync-processing-steps') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M3 5h14M3 10h14M3 15h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <circle cx="16" cy="15" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                        <span>Processing Steps</span>
                      </Link>
                      <Link to="/admin/v2-connectors" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/v2-connectors') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M7 7V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v3M6 7h8v4a4 4 0 0 1-8 0V7zM10 15v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Connectors</span>
                      </Link>
                      <Link to="/admin/legacy-ingress" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/legacy-ingress') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M3 10h9M9 6l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M13 3h3a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Legacy Ingress</span>
                      </Link>
                      <Link to="/admin/sync-diverter" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/sync-diverter') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <path d="M3 10h6c2 0 3-1 4-3l1-2M9 10c2 0 3 1 4 3l1 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 4h3v3M12 16h3v-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Sync Diverter</span>
                      </Link>
                      <Link to="/admin/streaming-clients" className={`sidebar-link sidebar-sublink sidebar-sublink-nested sidebar-sublink-deep ${isActive('/admin/streaming-clients') ? 'active' : ''}`}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="2" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M6.5 6.5a5 5 0 0 0 0 7M13.5 6.5a5 5 0 0 1 0 7M4 4a8.5 8.5 0 0 0 0 12M16 4a8.5 8.5 0 0 1 0 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Streaming Clients</span>
                      </Link>
                    </>
                  )}


                </div>
              )}
            </div>
          )}
        </nav>
      </aside>

      {/* Top Bar */}
      <header className="top-bar">
        <div className="top-bar-content">
<div className="top-bar-actions">
            {/* Partner display — partner users: picker ONLY when there is a choice to make.
                A single partner used to render as a static, non-clickable badge. It occupied the first
                slot of the breadcrumb, offered no action, and pushed the chain and hotel pickers — the
                controls the user actually operates — to the right. With one partner there is nothing to
                switch to, so the whole slot (badge and its trailing chevron) is dropped and the chain
                picker leads. Chains and hotels are unaffected; the partner is still applied, just not
                displayed. Multi-partner users keep the picker below. */}
            {usesPartnerSwitcher && !isSuperAdmin && partners.length > 1 && (
              <>
                <div className="partner-dropdown-container" ref={partnerDropdownRef}>
                  <button className="btn-select-partner" onClick={togglePartnerDropdown}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V18h14v-1.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                    </svg>
                    <span>{getSelectedPartnerName()}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={isPartnerDropdownOpen ? 'chevron-up' : ''}>
                      <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  {isPartnerDropdownOpen && (
                    <div className="partner-dropdown">
                      <div className="tenant-dropdown-header">
                        <div className="tenant-search-container">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tenant-search-icon">
                            <circle cx="7" cy="7" r="4" stroke="#6b7280" strokeWidth="2"/>
                            <path d="M10 10l3 3" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <input
                            type="text"
                            className="tenant-search-input"
                            placeholder="Search partners..."
                            value={partnerSearchQuery}
                            onChange={(e) => setPartnerSearchQuery(e.target.value)}
                            onKeyDown={partnerNav.onKeyDown}
                            autoFocus
                          />
                          {partnerSearchQuery && (
                            <button className="search-clear-btn" onClick={() => setPartnerSearchQuery('')}>
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M3 3l8 8M11 3l-8 8" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="tenant-dropdown-list">
                        {filteredPartners.map((p, i) => (
                          <button
                            key={p.partnerid}
                            {...partnerNav.navProps(i)}
                            className={`tenant-dropdown-item ${selectedPartner === p.partnerid ? 'active' : ''}${partnerNav.isActive(i) ? ' kb-active' : ''}`}
                            onClick={() => handlePartnerChange(p.partnerid)}
                          >
                            <div className="tenant-item-content">
                              <span className="tenant-item-name">{p.companyname || p.name}</span>
                              {p.country && <span className="tenant-item-code">{p.country}</span>}
                            </div>
                            {selectedPartner === p.partnerid && (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="topbar-breadcrumb-arrow">
                  <path d="M5 3l4 4-4 4" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </>
            )}

            {/* Partner picker — superadmins only */}
            {isSuperAdmin && (
              <>
                <div className="partner-dropdown-container" ref={partnerDropdownRef}>
                  <button className="btn-select-partner" onClick={togglePartnerDropdown}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V18h14v-1.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                    </svg>
                    <span>{getSelectedPartnerName()}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={isPartnerDropdownOpen ? 'chevron-up' : ''}>
                      <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>

                  {isPartnerDropdownOpen && (
                    <div className="partner-dropdown">
                      <div className="tenant-dropdown-header">
                        <div className="tenant-search-container">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tenant-search-icon">
                            <circle cx="7" cy="7" r="4" stroke="#6b7280" strokeWidth="2"/>
                            <path d="M10 10l3 3" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <input
                            type="text"
                            className="tenant-search-input"
                            placeholder="Search partners..."
                            value={partnerSearchQuery}
                            onChange={(e) => setPartnerSearchQuery(e.target.value)}
                            onKeyDown={partnerNav.onKeyDown}
                            autoFocus
                          />
                          {partnerSearchQuery && (
                            <button className="search-clear-btn" onClick={() => setPartnerSearchQuery('')}>
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M3 3l8 8M11 3l-8 8" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="tenant-dropdown-list">
                        {!partnerSearchQuery && (
                          <button
                            {...partnerNav.navProps(0)}
                            className={`tenant-dropdown-item ${!selectedPartner ? 'active' : ''}${partnerNav.isActive(0) ? ' kb-active' : ''}`}
                            onClick={() => handlePartnerChange(null)}
                          >
                            <div className="tenant-item-content">
                              <span className="tenant-item-name">All Partners</span>
                            </div>
                            {!selectedPartner && (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                        )}
                        {filteredPartners.length === 0 && (
                          <div className="tenant-dropdown-empty">
                            <p>No partners found</p>
                          </div>
                        )}
                        {filteredPartners.map((p, i) => {
                          const ni = i + (partnerHasAllRow ? 1 : 0);
                          return (
                          <button
                            key={p.partnerid}
                            {...partnerNav.navProps(ni)}
                            className={`tenant-dropdown-item ${selectedPartner === p.partnerid ? 'active' : ''}${partnerNav.isActive(ni) ? ' kb-active' : ''}`}
                            onClick={() => handlePartnerChange(p.partnerid)}
                          >
                            <div className="tenant-item-content">
                              <span className="tenant-item-name">{p.companyname || p.name}</span>
                              {p.country && <span className="tenant-item-code">{p.country}</span>}
                            </div>
                            {selectedPartner === p.partnerid && (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="topbar-breadcrumb-arrow">
                  <path d="M5 3l4 4-4 4" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </>
            )}

            {accounts.length === 1 ? (
              <div className="tenant-single">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="2" y="3" width="12" height="3" rx="1" fill="currentColor"/>
                  <rect x="2" y="7" width="12" height="3" rx="1" fill="currentColor"/>
                  <rect x="2" y="11" width="12" height="3" rx="1" fill="currentColor"/>
                </svg>
                <span>{getSelectedAccountName()}</span>
              </div>
            ) : (
              <div className="tenant-dropdown-container" ref={tenantDropdownRef}>
                <button className="btn-select-tenant" onClick={toggleTenantDropdown}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="2" y="3" width="12" height="3" rx="1" fill="currentColor"/>
                    <rect x="2" y="7" width="12" height="3" rx="1" fill="currentColor"/>
                    <rect x="2" y="11" width="12" height="3" rx="1" fill="currentColor"/>
                  </svg>
                  <span>{getSelectedAccountName()}</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={isTenantDropdownOpen ? 'chevron-up' : ''}>
                    <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>

                {isTenantDropdownOpen && (
                  <div className="tenant-dropdown">
                    <div className="tenant-dropdown-header">
                      <div className="tenant-search-container">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tenant-search-icon">
                          <circle cx="7" cy="7" r="4" stroke="#6b7280" strokeWidth="2"/>
                          <path d="M10 10l3 3" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <input
                          type="text"
                          className="tenant-search-input"
                          placeholder="Search hotel chains..."
                          value={tenantSearchQuery}
                          onChange={(e) => setTenantSearchQuery(e.target.value)}
                          onKeyDown={accountNav.onKeyDown}
                          autoFocus
                        />
                        {tenantSearchQuery && (
                          <button
                            className="search-clear-btn"
                            onClick={() => setTenantSearchQuery('')}
                          >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path d="M3 3l8 8M11 3l-8 8" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="tenant-dropdown-list">
                      {!tenantSearchQuery && (
                        <button
                          {...accountNav.navProps(0)}
                          className={`tenant-dropdown-item ${!selectedAccount ? 'active' : ''}${accountNav.isActive(0) ? ' kb-active' : ''}`}
                          onClick={() => handleAccountChange(null)}
                        >
                          <div className="tenant-item-content">
                            <span className="tenant-item-name">All Hotel Chains</span>
                          </div>
                          {!selectedAccount && (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      )}
                      {filteredAccounts.length === 0 && (
                        <div className="tenant-dropdown-empty">
                          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                            <circle cx="24" cy="24" r="20" stroke="#d1d5db" strokeWidth="2"/>
                            <path d="M16 24h16M24 16v16" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <p>No hotel chains found</p>
                          <span>Try a different search term</span>
                        </div>
                      )}
                      {filteredAccounts.map((account, i) => {
                        const ni = i + (tenantSearchQuery ? 0 : 1);
                        return (
                        <button
                          key={account.id}
                          {...accountNav.navProps(ni)}
                          className={`tenant-dropdown-item ${selectedAccount === account.id ? 'active' : ''}${accountNav.isActive(ni) ? ' kb-active' : ''}`}
                          onClick={() => handleAccountChange(account.id)}
                        >
                          <div className="tenant-item-content">
                            <span className="tenant-item-name">{account.name || 'Unnamed Account'}</span>
                            {account.username && <span className="tenant-item-code">{account.username}</span>}
                          </div>
                          {selectedAccount === account.id && (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Hotel picker — only when a chain is selected */}
            {selectedAccount && (
              <>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="topbar-breadcrumb-arrow">
                  <path d="M5 3l4 4-4 4" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="tenant-dropdown-container" ref={hotelDropdownRef}>
                  <button className="btn-select-tenant" onClick={() => { setIsHotelDropdownOpen(o => !o); if (isHotelDropdownOpen) setHotelSearchQuery(''); }}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M2 18V7l8-5 8 5v11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 18v-5h4v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{getSelectedHotelName()}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={isHotelDropdownOpen ? 'chevron-up' : ''}>
                      <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  {isHotelDropdownOpen && (
                    <div className="tenant-dropdown">
                      <div className="tenant-dropdown-header">
                        <div className="tenant-search-container">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="tenant-search-icon">
                            <circle cx="7" cy="7" r="4" stroke="#6b7280" strokeWidth="2"/>
                            <path d="M10 10l3 3" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <input
                            type="text"
                            className="tenant-search-input"
                            placeholder="Search hotels..."
                            value={hotelSearchQuery}
                            onChange={(e) => setHotelSearchQuery(e.target.value)}
                            onKeyDown={hotelNav.onKeyDown}
                            autoFocus
                          />
                          {hotelSearchQuery && (
                            <button className="search-clear-btn" onClick={() => setHotelSearchQuery('')}>
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M3 3l8 8M11 3l-8 8" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="tenant-dropdown-list">
                        {!hotelSearchQuery && (
                          <button
                            {...hotelNav.navProps(0)}
                            className={`tenant-dropdown-item ${!selectedHotel ? 'active' : ''}${hotelNav.isActive(0) ? ' kb-active' : ''}`}
                            onClick={() => handleHotelChange(null)}
                          >
                            <div className="tenant-item-content">
                              <span className="tenant-item-name">All Hotels</span>
                            </div>
                            {!selectedHotel && (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                        )}
                        {filteredHotels.length === 0 && (
                          <div className="tenant-dropdown-empty">
                            <p>No hotels found</p>
                          </div>
                        )}
                        {filteredHotels.map((h, i) => {
                          const ni = i + (hotelSearchQuery ? 0 : 1);
                          return (
                          <button
                            key={h.hotelid}
                            {...hotelNav.navProps(ni)}
                            className={`tenant-dropdown-item ${selectedHotel === h.hotelid ? 'active' : ''}${hotelNav.isActive(ni) ? ' kb-active' : ''}`}
                            onClick={() => handleHotelChange(h.hotelid)}
                          >
                            <div className="tenant-item-content">
                              <span className="tenant-item-name">{h.hotelname || 'Unnamed Hotel'}</span>
                              {h.hotelcode && <span className="tenant-item-code">{h.hotelcode}</span>}
                            </div>
                            {selectedHotel === h.hotelid && (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13 4L6 11L3 8" stroke="#4F7FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="user-dropdown-container" ref={userDropdownRef}>
              {isAuthenticated && user ? (
                <>
                  <button className="btn-user-menu" onClick={toggleUserDropdown}>
                    <div className="user-avatar">
                      {(user.email?.split('@')[0] || 'U')[0].toUpperCase()}
                    </div>
                    <span className="user-email">{user.email}</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={isUserDropdownOpen ? 'chevron-up' : ''}>
                      <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>

                  {isUserDropdownOpen && (
                    <div className="user-dropdown">
                      <button className="user-dropdown-item" onClick={handleProfileClick}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M2 13c0-3.5 2.5-5 6-5s6 1.5 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span>Profile</span>
                      </button>
                      <button className="user-dropdown-item" onClick={() => { setIsUserDropdownOpen(false); navigate('/invitations'); }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M2 4a1 1 0 011-1h10a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1V4z" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M2 4l6 5 6-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Invitations</span>
                      </button>
                      {(isSuperAdmin || isPartner || isAnyAdmin) && (
                        <button className="user-dropdown-item" onClick={() => { setIsUserDropdownOpen(false); navigate('/support'); }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M8 6a2 2 0 1 1 0 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            <circle cx="8" cy="12" r="0.75" fill="currentColor"/>
                          </svg>
                          <span>Support</span>
                        </button>
                      )}
                      <div className="user-dropdown-divider"></div>
                      <button className="user-dropdown-item" onClick={handleLogout}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <button className="btn-login" onClick={handleLoginClick}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 14h3a1 1 0 001-1V3a1 1 0 00-1-1h-3M6 11l3-3-3-3M2 8h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Log In</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
